import {
  createMongoAbility,
  MongoAbility,
  SubjectRawRule,
  SubjectType,
} from '@casl/ability';
import { createContextualCan } from '@casl/react';
import React from 'react';

type AbilityProviderProps = { children: React.ReactNode };

export type Rule = SubjectRawRule<
  string,
  SubjectType,
  Record<string | number | symbol, unknown>
>;

export enum AbilityActionKind {
  Update = 'UPDATE',
  Reset = 'RESET',
}

type Action =
  | {
      type: AbilityActionKind.Update;
      payload: Rule[];
    }
  | {
      type: AbilityActionKind.Reset;
    };

type Dispatch = (action: Action) => void;
type State = MongoAbility;

const AbilityContext = React.createContext<
  { state: State; dispatch: Dispatch } | undefined
>(undefined);

const abilityReducer = (_state: State, action: Action): State => {
  switch (action.type) {
    case AbilityActionKind.Update: {
      return createMongoAbility(action.payload);
    }
    case AbilityActionKind.Reset: {
      return createMongoAbility();
    }
    default: {
      throw new Error('Unhandled action type');
    }
  }
};

const AbilityInstanceContext =
  React.createContext<MongoAbility>(createMongoAbility());

export const Can = createContextualCan(AbilityInstanceContext.Consumer);

export const useAbilityContext = () => {
  const context = React.useContext(AbilityContext);
  if (context === undefined) {
    throw new Error('useAbilityContext must be used within a AbilityContext');
  }

  return context;
};

export const AbilityProvider = ({ children }: AbilityProviderProps) => {
  const [state, dispatch] = React.useReducer(
    abilityReducer,
    createMongoAbility(),
  );
  const value = { state, dispatch };

  return (
    <AbilityContext.Provider value={value}>
      <AbilityInstanceContext.Provider value={state}>
        {children}
      </AbilityInstanceContext.Provider>
    </AbilityContext.Provider>
  );
};
